// Language-independent card/role visuals: accent classes, suit + rank flavour,
// role colours, category structure. Text lives in the i18n copy module.
import type { CardKind, Role } from './types';

export const cardAccent: Record<CardKind, string> = {
  hack: 'a-magenta',
  duel: 'a-magenta',
  raid: 'a-magenta',
  emp: 'a-magenta',
  firewall: 'a-cyan',
  cipherShield: 'a-cyan',
  medPatch: 'a-cyan',
  rig: 'a-violet',
  overclock: 'a-violet',
  longwave: 'a-violet',
  downtime: 'a-amber',
  virus: 'a-amber',
  payday: 'a-lime',
  siphon: 'a-magenta',
  stim: 'a-cyan',
  trace: 'a-lime',
  airGap: 'a-cyan',
  killSwitch: 'a-amber',
  backchannel: 'a-lime',
  fieldPatch: 'a-cyan',
  coldReboot: 'a-violet',
  packetStorm: 'a-violet',
};

// Suit is fixed per category (colour-coded); rank varies per card instance so a
// deck of 14 firewalls doesn't read as "14 aces of hearts".
export const cardSuit: Record<CardKind, { glyph: string; color: string }> = {
  hack: { glyph: '♠', color: 'var(--magenta)' },
  duel: { glyph: '♠', color: 'var(--magenta)' },
  raid: { glyph: '♠', color: 'var(--magenta)' },
  emp: { glyph: '♠', color: 'var(--magenta)' },
  firewall: { glyph: '♥', color: 'var(--cyan)' },
  cipherShield: { glyph: '♥', color: 'var(--cyan)' },
  medPatch: { glyph: '♥', color: 'var(--cyan)' },
  rig: { glyph: '♣', color: 'var(--violet)' },
  longwave: { glyph: '♣', color: 'var(--violet)' },
  overclock: { glyph: '♣', color: 'var(--violet)' },
  downtime: { glyph: '♦', color: 'var(--amber)' },
  virus: { glyph: '♦', color: 'var(--amber)' },
  payday: { glyph: '✦', color: 'var(--lime)' },
  siphon: { glyph: '♠', color: 'var(--magenta)' },
  stim: { glyph: '♥', color: 'var(--cyan)' },
  trace: { glyph: '✦', color: 'var(--lime)' },
  airGap: { glyph: '♥', color: 'var(--cyan)' },
  killSwitch: { glyph: '♦', color: 'var(--amber)' },
  backchannel: { glyph: '✦', color: 'var(--lime)' },
  fieldPatch: { glyph: '♥', color: 'var(--cyan)' },
  coldReboot: { glyph: '♣', color: 'var(--violet)' },
  packetStorm: { glyph: '♣', color: 'var(--violet)' },
};

/** How many of each card the deck holds (mirrors DECK_SPEC in gamecore/cards.ts). */
export const deckCount: Record<CardKind, number> = {
  hack: 23,
  firewall: 13,
  medPatch: 2,
  payday: 4,
  raid: 4,
  emp: 1,
  duel: 4,
  cipherShield: 3,
  rig: 3,
  overclock: 2,
  longwave: 2,
  downtime: 2,
  virus: 2,
  siphon: 3,
  stim: 1,
  trace: 1,
  airGap: 2,
  killSwitch: 3,
  backchannel: 2,
  fieldPatch: 3,
  coldReboot: 2,
  packetStorm: 1,
};

const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

/** Deterministic per-card rank from its id (e.g. "firewall-3"), so copies differ. */
export function cardRank(cardId: string): string {
  const m = /-(\d+)$/.exec(cardId);
  const idx = m ? parseInt(m[1], 10) : cardId.length;
  return RANKS[idx % RANKS.length]!;
}

export const roleColor: Record<Role, string> = {
  director: 'var(--amber)',
  security: 'var(--cyan)',
  rebel: 'var(--magenta)',
  fixer: 'var(--lime)',
};

/** Public faction composition per player count (mirrors ROLE_TABLE in gamecore). */
export const roleComposition: Record<number, Record<Role, number>> = {
  4: { director: 1, security: 0, rebel: 2, fixer: 1 },
  5: { director: 1, security: 1, rebel: 2, fixer: 1 },
  6: { director: 1, security: 1, rebel: 3, fixer: 1 },
  7: { director: 1, security: 2, rebel: 3, fixer: 1 },
  8: { director: 1, security: 2, rebel: 3, fixer: 2 },
};

/** Fixed display order for factions in the roster. */
export const roleOrder: Role[] = ['director', 'security', 'rebel', 'fixer'];

export interface CategoryDef {
  key: 'attack' | 'defense' | 'gear' | 'sabotage' | 'economy' | 'recon';
  color: string;
  kinds: CardKind[];
}

export const categoryDefs: CategoryDef[] = [
  { key: 'attack', color: 'var(--magenta)', kinds: ['hack', 'duel', 'raid', 'siphon', 'emp'] },
  { key: 'defense', color: 'var(--cyan)', kinds: ['firewall', 'airGap', 'cipherShield', 'medPatch', 'fieldPatch', 'stim'] },
  { key: 'gear', color: 'var(--violet)', kinds: ['rig', 'longwave', 'overclock'] },
  { key: 'sabotage', color: 'var(--amber)', kinds: ['downtime', 'virus', 'killSwitch'] },
  { key: 'economy', color: 'var(--lime)', kinds: ['payday', 'backchannel', 'coldReboot', 'packetStorm'] },
  { key: 'recon', color: 'var(--lime)', kinds: ['trace'] },
];

/** Character names are proper nouns — same in every language. */
export const characterName: Record<string, string> = {
  cinder: 'Cinder', prism: 'Prism', bulwark: 'Bulwark', ember: 'Ember',
  mirage: 'Mirage', nomad: 'Nomad', twitch: 'Twitch', wraith: 'Wraith',
  specter: 'Specter', patch: 'Patch', golem: 'Golem', anchor: 'Anchor',
  rust: 'Rust', grit: 'Grit', aegis: 'Aegis', halo: 'Halo', fury: 'Fury',
  havoc: 'Havoc', longshot: 'Longshot', phantom: 'Phantom', scrap: 'Scrap',
};

/** Starting integrity (health) each character begins with, INCLUDING the
 *  bonusIntegrity ability (e.g. Bulwark 3+1=4). Mirrors functions characters.ts.
 *  Note: the Director role adds a further +1 on top, regardless of character. */
export const characterHp: Record<string, number> = {
  prism: 3, bulwark: 4, mirage: 3, twitch: 4, specter: 4, golem: 4,
  rust: 4, aegis: 3, fury: 3, longshot: 4, phantom: 3, scrap: 3,
  cinder: 3, ember: 4, nomad: 3, wraith: 3, patch: 3, anchor: 4,
  grit: 4, halo: 4, havoc: 4,
};

/** Display order for the How-to roster (matches the gamecore roster —
 *  every character has a unique ability). */
export const characterRoster: string[] = [
  'prism', 'bulwark', 'mirage', 'twitch', 'specter', 'golem',
  'rust', 'aegis', 'fury', 'longshot', 'phantom', 'scrap',
  'cinder', 'ember', 'nomad', 'wraith', 'patch', 'anchor',
  'grit', 'halo', 'havoc',
];

export const characterAbilityKey: Record<string, string> = {
  prism: 'drawOnHack', bulwark: 'bonusIntegrity', mirage: 'revive2', twitch: 'multiHack',
  specter: 'drawFromDiscard', golem: 'none', rust: 'drawOnDamage', aegis: 'innateShield',
  fury: 'noHackLimit', longshot: 'extraReach', phantom: 'evasive', scrap: 'drawWhenEmpty',
  cinder: 'pierceBlock', ember: 'bonusHeal', nomad: 'extraDraw', wraith: 'stealOnDamage',
  patch: 'regenChance', anchor: 'gearImmune', grit: 'handLimitBonus', halo: 'lucky',
  havoc: 'retaliate',
};
